import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, FloppyDisk, X, UploadSimple, Code, VideoCamera } from "@phosphor-icons/react";
import { StreamHLSPlayer } from "../../components/CMSPanel";
import BoxAnnotator from "../../components/BoxAnnotator";
import { uploadToCloudflareStreamTUS, TUS_THRESHOLD_BYTES } from "../../lib/streamTus";

const DEFAULT_BOXES = `[
  { "t": 1.0, "x": 0.30, "y": 0.20, "w": 0.15, "h": 0.20, "label": "scratch", "confidence": 0.94 },
  { "t": 2.5, "x": 0.55, "y": 0.40, "w": 0.10, "h": 0.15, "label": "dent", "confidence": 0.88 },
  { "t": 4.0, "x": 0.20, "y": 0.55, "w": 0.20, "h": 0.25, "label": "no_helmet", "confidence": 0.79 }
]`;

const BLANK = {
  title: "",
  scenario: "",
  location: "Shop floor · Cam 03",
  video_url: "",
  boxes: [],
  order: 0,
  published: true,
};

function safeParseBoxes(text) {
  try {
    const arr = JSON.parse(text || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

/**
 * Live Vision admin panel — manages the CCTV demo cards shown at /live-vision.
 *
 * Each demo is (video, list-of-detection-boxes). The video comes from the
 * same Cloudflare Stream direct-upload flow used for reels, but the boxes
 * are entered as JSON here (frame-by-frame `t/x/y/w/h/label/confidence`).
 *
 * Uploading a video:
 *  1. Ask backend for a CF direct upload URL (POST /admin/stream/upload-url)
 *  2. Upload file straight to CF
 *  3. Poll GET /admin/stream/{uid}/status until ready
 *  4. Write the HLS URL into the `video_url` field
 *
 * Boxes are edited as JSON in a textarea (a full visual annotator is a P2
 * — the JSON path is simpler and lets non-devs paste an inference log dump).
 */
export default function CCTVPanel({ token }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item being edited, or "new"
  const [draft, setDraft] = useState(BLANK);
  const [boxesText, setBoxesText] = useState("[]");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [mode, setMode] = useState("visual"); // 'visual' | 'json'
  const [visualBoxes, setVisualBoxes] = useState([]); // in-memory boxes with _id for annotator
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/cctv-demos?published_only=false`);
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setDraft({ ...BLANK });
    setBoxesText(DEFAULT_BOXES);
    setVisualBoxes(safeParseBoxes(DEFAULT_BOXES).map((b) => ({ ...b, _id: Math.random().toString(36).slice(2) })));
    setEditing("new");
  };

  const startEdit = (item) => {
    setDraft({ ...BLANK, ...item });
    setBoxesText(JSON.stringify(item.boxes || [], null, 2));
    setVisualBoxes((item.boxes || []).map((b) => ({ ...b, _id: Math.random().toString(36).slice(2) })));
    setEditing(item.id);
  };

  const cancel = () => { setEditing(null); setDraft(BLANK); setBoxesText("[]"); setVisualBoxes([]); };

  const parseBoxes = () => {
    try {
      const arr = JSON.parse(boxesText || "[]");
      if (!Array.isArray(arr)) throw new Error("boxes must be an array");
      for (const b of arr) {
        for (const k of ["t", "x", "y", "w", "h"]) {
          if (typeof b[k] !== "number") throw new Error(`box.${k} must be number`);
        }
        if (typeof b.label !== "string" || !b.label) throw new Error("box.label required");
      }
      return arr;
    } catch (e) {
      toast.error(`Bounding boxes JSON: ${e.message}`);
      return null;
    }
  };

  const save = async () => {
    let boxes;
    if (mode === "visual") {
      // Strip UI-only _id
      boxes = visualBoxes.map(({ _id, ...rest }) => rest);
    } else {
      boxes = parseBoxes();
      if (boxes === null) return;
    }
    const payload = { ...draft, boxes };
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/cctv-demos`, payload, { headers: { "X-Admin-Token": token } });
        toast.success("Demo created.");
      } else {
        await axios.put(`${API}/admin/cctv-demos/${editing}`, payload, { headers: { "X-Admin-Token": token } });
        toast.success("Demo updated.");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this demo?")) return;
    await axios.delete(`${API}/admin/cctv-demos/${id}`, { headers: { "X-Admin-Token": token } });
    toast.success("Deleted.");
    load();
  };

  const handleStreamUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress({ pct: 0, state: "uploading" });

    // Route large files through TUS resumable direct-to-CF upload
    if (file.size >= TUS_THRESHOLD_BYTES) {
      try {
        const result = await uploadToCloudflareStreamTUS(file, token, setProgress);
        setDraft((d) => ({ ...d, video_url: result.hls_url }));
        toast.success("Large file uploaded via TUS resumable — CF Stream ready.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || "TUS upload failed.");
      } finally {
        setUploading(false);
        setProgress(null);
        if (fileRef.current) fileRef.current.value = "";
      }
      return;
    }

    try {
      // Server-proxied CF Stream upload (browser → wisewit.ai → cloudflarestream.com)
      // — bypasses corporate-network TLS-inspection issues.
      const fd = new FormData();
      fd.append("file", file);
      const { data: migrated } = await axios.post(`${API}/admin/stream/migrate`, fd, {
        headers: { "X-Admin-Token": token },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setProgress({ pct: Math.round((evt.loaded / evt.total) * 100), state: "uploading" });
        },
        timeout: 0,
      });
      const uid = migrated?.uid;
      if (!uid) throw new Error("No UID from backend");
      setProgress({ pct: 100, state: "encoding" });
      let ready = null;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data: st } = await axios.get(`${API}/admin/stream/${uid}/status`, { headers: { "X-Admin-Token": token } });
          if (st.ready) { ready = st; break; }
        } catch { /* keep polling */ }
      }
      if (!ready) throw new Error("Encoding timed out.");
      setDraft((d) => ({ ...d, video_url: ready.hls_url }));
      toast.success("Video ready on Cloudflare Stream.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Upload failed.");
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="cctv-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-overline mb-2">◢ Live Vision demos ({items.length})</div>
          <div className="text-[#8E8E93] text-sm">Cloudflare-hosted footage + bounding-box overlays, shown on <a href="/live-vision" className="text-[#DFFF00]" target="_blank" rel="noreferrer">/live-vision</a>.</div>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="cctv-new" className="btn-primary"><Plus size={14} /> New demo</button>
        )}
      </div>

      {editing && (
        <div className="border border-[#2A2A2A] p-6 mb-8 bg-[#0a0a0a]" data-testid="cctv-editor">
          <div className="flex items-center justify-between mb-4">
            <div className="text-overline">◢ {editing === "new" ? "New" : "Edit"} demo</div>
            <div className="flex items-center gap-2">
              <button onClick={cancel} className="btn-ghost"><X size={14} /> Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="cctv-save"><FloppyDisk size={14} /> Save</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="ww-label">Title</div>
              <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="ww-input" data-testid="cctv-title" />
            </label>
            <label className="block">
              <div className="ww-label">Scenario (overline)</div>
              <input value={draft.scenario} onChange={(e) => setDraft({ ...draft, scenario: e.target.value })} className="ww-input" placeholder="Aluminum extrusion QC" />
            </label>
            <label className="block md:col-span-2">
              <div className="ww-label">Location tag</div>
              <input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} className="ww-input" placeholder="Shop floor · Cam 03" />
            </label>

            <div className="md:col-span-2">
              <div className="ww-label">Video</div>
              <input type="file" ref={fileRef} accept="video/*" hidden onChange={handleStreamUpload} />
              <div className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary" data-testid="cctv-upload">
                  <UploadSimple size={12} /> {uploading ? "Uploading to Cloudflare…" : "Upload video to Cloudflare Stream"}
                </button>
                {progress && (
                  <div className="flex-1 min-w-[140px] max-w-xs">
                    <div className="h-1 bg-[#1A1A1A] overflow-hidden">
                      <div className="h-full bg-[#DFFF00] transition-[width] duration-200" style={{ width: `${progress.pct}%` }} />
                    </div>
                    <div className="mt-1 font-mono text-[9px] tracking-[0.18em] uppercase text-[#8E8E93]">
                      {progress.state === "uploading" && `Uploading ${progress.pct}%`}
                      {progress.state === "encoding" && "Encoding on Cloudflare…"}
                    </div>
                  </div>
                )}
                {draft.video_url && <span className="font-mono text-[10px] text-[#DFFF00] truncate max-w-[60%]">{draft.video_url}</span>}
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <div className="ww-label mb-0">Bounding boxes</div>
                <div className="flex items-center gap-1 border border-[#2A2A2A] p-1">
                  <button
                    type="button"
                    onClick={() => {
                      // If switching from JSON → visual, load JSON into visualBoxes.
                      if (mode === "json") {
                        const parsed = safeParseBoxes(boxesText);
                        setVisualBoxes(parsed.map((b) => ({ ...b, _id: Math.random().toString(36).slice(2) })));
                      }
                      setMode("visual");
                    }}
                    className={`px-2 py-1 text-[10px] font-mono tracking-[0.18em] uppercase ${mode === "visual" ? "bg-[#DFFF00] text-black" : "text-[#8E8E93] hover:text-white"}`}
                    data-testid="cctv-mode-visual"
                  >
                    <VideoCamera size={10} weight="fill" style={{ display: "inline", marginRight: 4 }} /> Visual
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // Switching visual → JSON: dump visualBoxes into the text.
                      if (mode === "visual") {
                        const clean = visualBoxes.map(({ _id, ...rest }) => rest);
                        setBoxesText(JSON.stringify(clean, null, 2));
                      }
                      setMode("json");
                    }}
                    className={`px-2 py-1 text-[10px] font-mono tracking-[0.18em] uppercase ${mode === "json" ? "bg-[#DFFF00] text-black" : "text-[#8E8E93] hover:text-white"}`}
                    data-testid="cctv-mode-json"
                  >
                    <Code size={10} weight="fill" style={{ display: "inline", marginRight: 4 }} /> JSON
                  </button>
                </div>
              </div>

              {mode === "visual" ? (
                <BoxAnnotator videoUrl={draft.video_url} boxes={visualBoxes} onChange={setVisualBoxes} />
              ) : (
                <>
                  <textarea
                    value={boxesText}
                    onChange={(e) => setBoxesText(e.target.value)}
                    rows={12}
                    className="ww-input font-mono text-xs"
                    spellCheck={false}
                    data-testid="cctv-boxes"
                  />
                  <div className="mt-2 font-mono text-[10px] text-[#8E8E93]">
                    Fields per box: <code className="text-white">t</code> (seconds), <code className="text-white">x, y, w, h</code> (0..1 fraction of video rect), <code className="text-white">label</code> (string), <code className="text-white">confidence</code> (0..1), optional <code className="text-white">color</code> (hex).
                  </div>
                </>
              )}
            </div>

            <label className="block">
              <div className="ww-label">Order</div>
              <input type="number" value={draft.order} onChange={(e) => setDraft({ ...draft, order: parseInt(e.target.value || 0) })} className="ww-input" />
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} />
              <span className="text-[#D7D7D9] text-sm">Published (visible on /live-vision)</span>
            </label>
          </div>

          {draft.video_url && (
            <div className="mt-6">
              <div className="text-overline mb-2">◢ Preview</div>
              <div className="max-w-md border border-[#2A2A2A]">
                <StreamHLSPlayer src={draft.video_url} />
              </div>
            </div>
          )}
        </div>
      )}

      {!editing && (
        <div className="border border-[#2A2A2A]">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0a0a0a] font-mono text-[10px] tracking-[0.18em] uppercase text-[#8E8E93]">
            <div className="col-span-4">Title</div>
            <div className="col-span-3">Scenario</div>
            <div className="col-span-2">Boxes</div>
            <div className="col-span-1">Live</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {items.length === 0 && <div className="px-4 py-10 text-[#555] text-sm text-center">No demos yet. Click "New demo".</div>}
          {items.map((it) => (
            <div key={it.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-[#2A2A2A] text-sm items-center">
              <div className="col-span-4 text-white">{it.title}</div>
              <div className="col-span-3 text-[#8E8E93]">{it.scenario}</div>
              <div className="col-span-2 font-mono text-xs">{it.boxes?.length || 0}</div>
              <div className="col-span-1 font-mono text-xs" style={{ color: it.published ? "#DFFF00" : "#8E8E93" }}>{it.published ? "yes" : "no"}</div>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={() => startEdit(it)} className="p-2 border border-[#2A2A2A] hover:border-[#DFFF00]" title="Edit"><PencilSimple size={12} /></button>
                <button onClick={() => remove(it.id)} className="p-2 border border-[#2A2A2A] hover:border-[#FF3060]" title="Delete"><Trash size={12} color="#FF3060" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
